import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save, Trash2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

interface Requisito {
  id: string;
  tipoRegistro: "TITULAR" | "TRAMITADOR";
  tipoPersona: "FISICA" | "MORAL" | null;
  sectorId: string | null;
  sectorCodigo: string | null;
  sectorNombre: string | null;
  tipoDocumentoId: string;
  tipoDocumentoCodigo: string;
  tipoDocumentoNombre: string;
  exigeCopiaCertificada: boolean;
  obligatorio: boolean;
  observacion: string | null;
}
interface Sector {
  id: string;
  codigo: string;
  nombre: string;
}
interface TipoDoc {
  id: string;
  codigo: string;
  nombre: string;
}

type EditorState = { mode: "new" } | { mode: "edit"; row: Requisito } | null;

const blank = {
  tipoRegistro: "TITULAR" as "TITULAR" | "TRAMITADOR",
  tipoPersona: "" as "" | "FISICA" | "MORAL",
  sectorId: "",
  tipoDocumentoId: "",
  exigeCopiaCertificada: false,
  obligatorio: true,
  observacion: "",
};

export function RequisitosDocumentalesPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["requisitos-documentales"],
    queryFn: async () => (await api.get<Requisito[]>("/api/admin/requisitos-documentales")).data,
  });
  const sectores = useQuery({
    queryKey: ["requisitos-documentales", "sectores"],
    queryFn: async () => (await api.get<Sector[]>("/api/admin/requisitos-documentales/sectores")).data,
  });
  const tipos = useQuery({
    queryKey: ["tipos-documento"],
    queryFn: async () => (await api.get<TipoDoc[]>("/api/documentos/tipos")).data,
  });

  const [editor, setEditor] = useState<EditorState>(null);
  const [filtroTR, setFiltroTR] = useState<"" | "TITULAR" | "TRAMITADOR">("");
  const [filtroSector, setFiltroSector] = useState<string>("");

  const filas = useMemo(() => {
    const data = list.data ?? [];
    return data.filter((r) => {
      if (filtroTR && r.tipoRegistro !== filtroTR) return false;
      if (filtroSector === "__none__" && r.sectorId !== null) return false;
      if (filtroSector && filtroSector !== "__none__" && r.sectorId !== filtroSector) return false;
      return true;
    });
  }, [list.data, filtroTR, filtroSector]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Requisitos documentales</h1>
          <p className="text-sm text-fg-muted">
            Matriz por tipo de registro × persona × sector. Define qué documentos exige cada combinación
            y cuáles deben ser copia certificada original. Los cambios quedan en bitácora.
          </p>
        </div>
        <button
          onClick={() => setEditor({ mode: "new" })}
          className="inline-flex cursor-pointer items-center gap-2 rounded bg-primary px-3 py-2 text-sm font-medium text-white transition hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" /> Nuevo requisito
        </button>
      </header>

      <div className="flex flex-wrap gap-3 rounded-lg border bg-surface p-3 text-sm">
        <label>
          Tipo de registro:{" "}
          <select
            value={filtroTR}
            onChange={(e) => setFiltroTR(e.target.value as typeof filtroTR)}
            className="rounded border px-2 py-1"
          >
            <option value="">Todos</option>
            <option value="TITULAR">TITULAR</option>
            <option value="TRAMITADOR">TRAMITADOR</option>
          </select>
        </label>
        <label>
          Sector:{" "}
          <select
            value={filtroSector}
            onChange={(e) => setFiltroSector(e.target.value)}
            className="rounded border px-2 py-1"
          >
            <option value="">Todos</option>
            <option value="__none__">Sin sector (cualquiera)</option>
            {sectores.data?.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </label>
        <span className="ml-auto text-fg-subtle">
          {filas.length} de {list.data?.length ?? 0}
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="overflow-x-auto rounded-lg border bg-surface shadow-card">
            <div className="w-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-xs uppercase tracking-wide text-fg-subtle">
                <tr>
                  <th className="px-3 py-2 text-left">Tipo registro</th>
                  <th className="px-3 py-2 text-left">Persona</th>
                  <th className="px-3 py-2 text-left">Sector</th>
                  <th className="px-3 py-2 text-left">Documento</th>
                  <th className="px-3 py-2 text-left">Flags</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {list.isLoading && (
                  <tr><td colSpan={6} className="p-4 text-fg-subtle">Cargando…</td></tr>
                )}
                {filas.map((r) => (
                  <tr key={r.id} className="transition hover:bg-surface-2">
                    <td className="px-3 py-2 font-medium">{r.tipoRegistro}</td>
                    <td className="px-3 py-2">{r.tipoPersona ?? <em className="text-fg-faint">cualquiera</em>}</td>
                    <td className="px-3 py-2">
                      {r.sectorNombre ?? <em className="text-fg-faint">cualquiera</em>}
                    </td>
                    <td className="px-3 py-2">
                      <div>{r.tipoDocumentoNombre}</div>
                      <div className="text-xs text-fg-subtle font-mono">{r.tipoDocumentoCodigo}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>{r.obligatorio ? "Obligatorio" : "Opcional"}</div>
                      {r.exigeCopiaCertificada && <div className="text-estado-warn">Copia certificada</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setEditor({ mode: "edit", row: r })}
                        className="cursor-pointer rounded border px-2 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
                {!list.isLoading && filas.length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-fg-subtle">Sin filas para los filtros.</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4">
          {editor ? (
            <Editor
              key={editor.mode === "edit" ? editor.row.id : "new"}
              initial={editor.mode === "edit" ? editor.row : null}
              sectores={sectores.data ?? []}
              tipos={tipos.data ?? []}
              onCancel={() => setEditor(null)}
              onSaved={async () => {
                setEditor(null);
                await qc.invalidateQueries({ queryKey: ["requisitos-documentales"] });
              }}
            />
          ) : (
            <div className="rounded-lg border bg-surface p-4 text-sm text-fg-subtle">
              Selecciona "Nuevo requisito" o "Editar" en una fila para abrir el formulario.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Editor({
  initial,
  sectores,
  tipos,
  onCancel,
  onSaved,
}: {
  initial: Requisito | null;
  sectores: Sector[];
  tipos: TipoDoc[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    tipoRegistro: initial?.tipoRegistro ?? blank.tipoRegistro,
    tipoPersona: (initial?.tipoPersona ?? "") as "" | "FISICA" | "MORAL",
    sectorId: initial?.sectorId ?? "",
    tipoDocumentoId: initial?.tipoDocumentoId ?? "",
    exigeCopiaCertificada: initial?.exigeCopiaCertificada ?? false,
    obligatorio: initial?.obligatorio ?? true,
    observacion: initial?.observacion ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        tipoRegistro: form.tipoRegistro,
        tipoPersona: form.tipoPersona || null,
        sectorId: form.sectorId || null,
        tipoDocumentoId: form.tipoDocumentoId,
        exigeCopiaCertificada: form.exigeCopiaCertificada,
        obligatorio: form.obligatorio,
        observacion: form.observacion.trim() || null,
      };
      if (initial) {
        await api.put(`/api/admin/requisitos-documentales/${initial.id}`, body);
      } else {
        await api.post("/api/admin/requisitos-documentales", body);
      }
    },
    onSuccess: onSaved,
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string; message?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.message || (e as Error).message);
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!initial) return;
      if (!window.confirm(`Eliminar requisito de ${initial.tipoDocumentoNombre}?`)) return;
      await api.delete(`/api/admin/requisitos-documentales/${initial.id}`);
    },
    onSuccess: onSaved,
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || (e as Error).message);
    },
  });

  return (
    <div className="space-y-3 rounded-lg border bg-surface p-4 shadow-card">
      <h2 className="text-base font-semibold">
        {initial ? "Editar requisito" : "Nuevo requisito"}
      </h2>

      <Lbl label="Tipo de registro">
        <select
          value={form.tipoRegistro}
          onChange={(e) => setForm({ ...form, tipoRegistro: e.target.value as "TITULAR" | "TRAMITADOR" })}
          className="w-full rounded border px-2 py-1 text-sm"
        >
          <option value="TITULAR">TITULAR</option>
          <option value="TRAMITADOR">TRAMITADOR</option>
        </select>
      </Lbl>

      <Lbl label="Tipo de persona (vacío = cualquiera)">
        <select
          value={form.tipoPersona}
          onChange={(e) => setForm({ ...form, tipoPersona: e.target.value as "" | "FISICA" | "MORAL" })}
          className="w-full rounded border px-2 py-1 text-sm"
        >
          <option value="">Cualquiera</option>
          <option value="FISICA">FISICA</option>
          <option value="MORAL">MORAL</option>
        </select>
      </Lbl>

      <Lbl label="Sector (vacío = cualquiera)">
        <select
          value={form.sectorId}
          onChange={(e) => setForm({ ...form, sectorId: e.target.value })}
          className="w-full rounded border px-2 py-1 text-sm"
        >
          <option value="">Cualquiera</option>
          {sectores.map((s) => (
            <option key={s.id} value={s.id}>{s.nombre}</option>
          ))}
        </select>
      </Lbl>

      <Lbl label="Tipo de documento *">
        <select
          value={form.tipoDocumentoId}
          onChange={(e) => setForm({ ...form, tipoDocumentoId: e.target.value })}
          className="w-full rounded border px-2 py-1 text-sm"
        >
          <option value="">— Selecciona —</option>
          {tipos.map((t) => (
            <option key={t.id} value={t.id}>{t.nombre}</option>
          ))}
        </select>
      </Lbl>

      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.obligatorio}
            onChange={(e) => setForm({ ...form, obligatorio: e.target.checked })}
          />
          Obligatorio
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.exigeCopiaCertificada}
            onChange={(e) => setForm({ ...form, exigeCopiaCertificada: e.target.checked })}
          />
          Exige copia certificada original
        </label>
      </div>

      <Lbl label="Observación">
        <textarea
          value={form.observacion}
          onChange={(e) => setForm({ ...form, observacion: e.target.value })}
          rows={3}
          className="w-full rounded border px-2 py-1 text-sm"
          placeholder="Notas para Calidad o el solicitante"
        />
      </Lbl>

      {error && (
        <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        {initial ? (
          <button
            onClick={() => remove.mutate()}
            disabled={remove.isPending || save.isPending}
            className="inline-flex cursor-pointer items-center gap-1 rounded border border-estado-err/25 px-2 py-1 text-xs text-estado-err transition hover:bg-estado-err-bg disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" /> Eliminar
          </button>
        ) : <span />}
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={save.isPending}
            className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              if (!form.tipoDocumentoId) {
                setError("Selecciona un tipo de documento.");
                return;
              }
              setError(null);
              save.mutate();
            }}
            disabled={save.isPending}
            className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {save.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
